import React from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import {
	Box, Breadcrumbs,
	CircularProgress,
	Typography
} from "@mui/material";
import {ANALYZE_CHANGES, DELETE_FORM_SETUP, DELETE_QUESTION_SETUP, PUT_FORM_SETUP, PUT_QUESTION_SETUP} from "../Queries";
import {DifferenceSet} from "../models/definitions/DifferenceSet";
import {QuestionSetup} from "../models/definitions/QuestionSetup";
import {deepClone, normalizeData} from "../../../libraries/util/Functions";
import {
	openQuestionDetailsForDefinition,
	openQuestionDetailsForNewDefinition
} from "../questions/DynamicFormsQuestionDetails";
import {FormSetup} from "../models/definitions/FormSetup";
import {openFormDetailsForDefinition, openFormDetailsForNewDefinition} from "../forms/DynamicFormsFormDetails";
import {Link} from "react-router-dom";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {cloneSetup} from "../Utils";
import {DeployDisplay} from "../../../components/common/deploy/DeployDisplay";
import {translateFunction} from "../../../localization/TranslationService";

const translate = translateFunction("SureBasePortalDynamicForms");

export function DynamicFormsChangesOverview() {
	const query = useQuery(ANALYZE_CHANGES, {
		client: graphQLServices.dynamicFormsClient,
		fetchPolicy: "no-cache"
	});
	
	if (query.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (query.error) {
		throw query.error;
	}
	
	if (!query.data) {
		throw new Error("Missing data!");
	}
	
	const differenceSet: DifferenceSet = normalizeData(deepClone(query.data.analyzeChanges));
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/dynamicforms">{translate("Dynamic Forms")}</Link>
			<Typography color="text.primary">{translate("Changes")}</Typography>
		</Breadcrumbs>}
		center={<div style={{overflowY: "auto", height: "100%"}}>
			{differenceSet.forms.map((set,index) => {
				return <DeployDisplay<FormSetup>
					name={translate("Form Setup")}
					stages={differenceSet.stages}
					items={set}
					keyMapper={it => it.formId}
					key={`forms-${index}`}
					putItem={putFormSetup}
					deleteItem={deleteFormSetup}
					openItemDetails={openFormDetailsForDefinition}
					openNewItemDetails={openFormDetailsForNewDefinition}
				/>;
			})}
			{differenceSet.questions.map((set,index)  => {
				return <DeployDisplay<QuestionSetup>
					name={translate("Question Setup")}
					stages={differenceSet.stages}
					items={set}
					keyMapper={it => it.fullId}
					key={`questionSetup-${index}`}
					putItem={putQuestionSetup}
					deleteItem={deleteQuestionSetup}
					openItemDetails={openQuestionDetailsForDefinition}
					openNewItemDetails={openQuestionDetailsForNewDefinition}
				/>;
			})}
		</div>}
	/>;
}

async function putFormSetup(stage: string, item: FormSetup) {
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: PUT_FORM_SETUP,
		variables: {
			stage: stage,
			formSetup: cloneSetup(item),
		}
	});
}

async function deleteFormSetup(stage: string, itemId: string) {
	const [definition, frontEndName, clientName] = itemId.split(":");
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: DELETE_FORM_SETUP,
		variables: {
			stage: stage,
			formName: definition,
			frontEndName: frontEndName,
			clientName: clientName,
		}
	});
}

async function putQuestionSetup(stage: string, item: QuestionSetup) {
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: PUT_QUESTION_SETUP,
		variables: {
			stage: stage,
			questionSetup: cloneSetup(item),
		}
	});
}

async function deleteQuestionSetup(stage: string, itemId: string) {
	const [definition, frontEndName, clientName] = itemId.split(":");
	await graphQLServices.dynamicFormsClient.mutate({
		mutation: DELETE_QUESTION_SETUP,
		variables: {
			stage: stage,
			questionDefinition: definition,
			frontEndName: frontEndName,
			clientName: clientName,
		}
	});
}

export const DynamicFormsChangesOverviewPage = pageAuthorizedByGroup(DynamicFormsChangesOverview, ["Admin", "DynamicForms"]);
